import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { formatDate, formatDateTime } from '../lib/format'
import { candidateId, hasCadence } from '../lib/scheduled-generation'
import { cadenceSummary } from '../lib/cadence-picker'
import { nextOccurrenceOnOrAfter, type CadenceRule } from '../lib/cadence'
import { localDate } from '../lib/campaigns'
import { describeScheduledAudience, type ScheduledAudienceRule } from '../lib/scheduled-audience'

type Campaign = {
  id: string
  name: string | null
  subject: string
  body: string
  cadence: unknown
  meeting_time: string | null
  is_enabled: boolean
  /**
   * `column not null default '{}'::jsonb` (see `scheduled-sends-generation.md`),
   * so this is never actually `null` — but an ordinary `{}` (no `tab`) is
   * possible in principle for any row this screen hasn't asked for it
   * before, so `tab` is checked before this is trusted as a real rule
   * rather than assumed present just because the column always is.
   */
  audience_rule: Partial<ScheduledAudienceRule>
}

/** A blast `generate-scheduled.ts` produced from a template — see
 *  `campaigns.generated_from_id`, added 2026-08-21. */
type Generated = {
  id: string
  subject: string
  audience: { agent_ids?: string[] } | null
  generated_from_id: string
  created_at: string
}
type OccState = { status: string; recipient_count: number | null }

/** Recurring templates only — `hasCadence` is the one shared test for this,
 *  also used by `generate-scheduled.ts`. */
const isRecurring = (c: Campaign) => hasCadence(c.cadence)

/**
 * The recurring campaigns themselves — build them on the roster (Email,
 * then "Repeat this on a schedule" directly, not "Same message to
 * everybody"), manage them here.
 *
 * Chris, 2026-08-21: this tab is where they *live* — cancel or delete a
 * recurring campaign here if she needs to end it and do something
 * different. What it PRODUCES each time it fires shows up in the Outbox
 * (moved there from Outreach 2026-08-22, so every automated category —
 * milestones, drips, scheduled sends — is checked in exactly one place) —
 * see `Outbox.tsx`'s `HeldBulkSends` — so that is not duplicated here;
 * this screen is about the standing rule, not today's instance of it.
 */
export default function Scheduled() {
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null)
  const [generated, setGenerated] = useState<Generated[]>([])
  const [occurrences, setOccurrences] = useState<Map<string, OccState>>(new Map())
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [c, g, o] = await Promise.all([
      supabase
        .from('campaigns')
        .select('id,name,subject,body,cadence,meeting_time,is_enabled,audience_rule'),
      supabase.from('campaigns').select('id,subject,audience,generated_from_id,created_at').not('generated_from_id', 'is', null),
      supabase.from('campaign_occurrences').select('campaign_id,status,recipient_count'),
    ])
    const err = c.error ?? g.error ?? o.error
    if (err) return setError(err.message)
    setCampaigns(((c.data ?? []) as unknown as Campaign[]).filter(isRecurring))
    setGenerated((g.data ?? []) as unknown as Generated[])
    setOccurrences(
      new Map(
        ((o.data ?? []) as { campaign_id: string; status: string; recipient_count: number | null }[]).map((r) => [
          r.campaign_id,
          { status: r.status, recipient_count: r.recipient_count },
        ]),
      ),
    )
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const setEnabled = async (id: string, enabled: boolean) => {
    setBusy(id + 'enable')
    const { error: err } = await supabase.from('campaigns').update({ is_enabled: enabled }).eq('id', id)
    setBusy(null)
    if (err) return setError(err.message)
    void load()
  }

  const remove = async (id: string) => {
    setBusy(id + 'del')
    const { error: err } = await supabase.from('campaigns').delete().eq('id', id)
    setBusy(null)
    setConfirmDelete(null)
    if (err) return setError(err.message)
    void load()
  }

  if (error) return <div className="empty">{error}</div>
  if (!campaigns) return <div className="empty">Loading…</div>

  return (
    <div className="board">
      <div className="notice">
        Recurring campaigns. Each time one fires, it produces a normal bulk send — those show up
        in the Outbox for her to review and send, and stay reachable from Bulk. This screen is
        for the standing rule: pause or end one here if it needs to stop.
      </div>

      {campaigns.length === 0 && (
        <div className="hint">
          {/*
            Corrected 2026-08-22 — the previous wording ("choose 'Same message
            to everybody', then 'Repeat on a schedule'") described a sequence
            that does not exist in `Email.tsx` and was the likely cause of an
            early click-through appearing broken. The three "how" choices are
            siblings, not steps: picking "Same message to everybody" commits
            to a one-off blast immediately and never offers a repeat option
            afterward.
          */}
          No recurring campaigns yet. Build one from the roster: filter to the group you want,
          press Email, then choose "Repeat this on a schedule" directly — not "Same message to
          everybody" first.
        </div>
      )}

      {campaigns.map((c) => {
        const mine = generated.filter((g) => g.generated_from_id === c.id)
        /**
         * Blocks delete while something it produced is still sitting unsent.
         * `generated_from_id` is `on delete set null` (see the migration), so
         * deleting the template would not delete that blast — it would just
         * quietly vanish from this screen's own tracking, with no way to find
         * it again from here. It would still show up in the "Bulk sends"
         * section on the Outbox (moved there from Outreach 2026-08-22,
         * renamed and broadened 2026-08-23 — see `loadHeldBulkSends` in
         * `outbox.ts`, which reads `id.startsWith('manual-')`, not this
         * column, to decide what counts as one of these), just no longer
         * traceable back to the template that produced it. Send it or pause
         * the template instead; delete once nothing of its is left waiting.
         */
        const pending = mine.filter((g) => occurrences.get(g.id)?.status !== 'sent')

        /**
         * The three details Chris asked for after creating his first live
         * campaign (2026-08-22): the schedule in readable terms, when it
         * next fires, and who it's for. All three re-use logic that already
         * existed elsewhere rather than adding new rules — `cadenceSummary`
         * is what the create-flow's own live summary uses, `describeScheduledAudience`
         * reads the same `TAB_LABEL`/`FILTERS` labels the rest of the app
         * does, and `nextOccurrenceOnOrAfter` is `cadence.ts`'s own answer to
         * this exact question.
         */
        const cadenceRule = c.cadence as CadenceRule
        const today = localDate(new Date())
        const rawNext = c.is_enabled ? nextOccurrenceOnOrAfter(cadenceRule, today) : null
        /**
         * If the cadence says "today" but today's blast already exists —
         * `shouldFireNow` fires from the moment `fire_time` passes until
         * midnight, so a firing earlier today is common, not an edge case —
         * showing "today" as the NEXT send would be telling her it's still
         * to come when it has already gone out. Steps one day forward and
         * asks again instead, rather than just labelling today done and
         * leaving the actual next date unanswered.
         */
        const todayAlreadyGenerated = rawNext === today && mine.some((g) => g.id === candidateId({ id: c.id }, today))
        /**
         * Tomorrow, as a calendar date — not "today + 24h of epoch time".
         * `new Date(today).getTime() + 86400000` breaks on a DST transition
         * (the US falls back on 2026-11-01, so adding a fixed 24h from that
         * date's midnight lands back on 2026-11-01 instead of 2026-11-02).
         * `cadence.ts`'s own `nextOccurrenceOnOrAfter` avoids exactly this by
         * stepping through `Date` fields (`getFullYear`/`getMonth`/`getDate`)
         * rather than through milliseconds — this does the same.
         */
        const nextDate = todayAlreadyGenerated
          ? nextOccurrenceOnOrAfter(
              cadenceRule,
              (() => {
                const [y, m, d] = today.split('-').map(Number)
                return localDate(new Date(y, m - 1, d + 1))
              })(),
            )
          : rawNext
        const audienceRule = c.audience_rule
        const audience = audienceRule.tab
          ? describeScheduledAudience({
              tab: audienceRule.tab,
              filters: audienceRule.filters ?? {},
              search: audienceRule.search ?? '',
            })
          : 'Not recorded — saved before this was tracked'

        return (
          <section className="sec" key={c.id} style={{ marginBottom: 16 }}>
            <h3>
              {c.name || c.id}
              {!c.is_enabled && <span className="badge dep">PAUSED</span>}
            </h3>
            <div className="row">
              <span className="k">Subject</span>
              <span className="v">{c.subject}</span>
            </div>
            <div className="row">
              <span className="k">Schedule</span>
              <span className="v">
                {cadenceSummary(cadenceRule)} at {c.meeting_time ?? 'not set'} America/New York
              </span>
            </div>
            <div className="row">
              <span className="k">Next send</span>
              <span className="v">
                {!c.is_enabled
                  ? 'Paused — resume to schedule the next one'
                  : nextDate
                    ? `${formatDate(nextDate)}${todayAlreadyGenerated ? " (today's has already gone out)" : ''}`
                    : 'Could not be worked out — check the cadence rule'}
              </span>
            </div>
            <div className="row">
              <span className="k">Sends to</span>
              <span className="v">{audience}</span>
            </div>
            <div className="dbody" style={{ marginTop: 10 }}>{c.body}</div>

            <div className="savebar" style={{ marginTop: 12 }}>
              {c.is_enabled ? (
                <button className="btn ghost sm" disabled={!!busy} onClick={() => void setEnabled(c.id, false)}>
                  {busy === c.id + 'enable' ? 'Pausing…' : 'Pause'}
                </button>
              ) : (
                <button className="btn sm" disabled={!!busy} onClick={() => void setEnabled(c.id, true)}>
                  {busy === c.id + 'enable' ? 'Resuming…' : 'Resume'}
                </button>
              )}
              {pending.length > 0 ? (
                <span className="hint">
                  {pending.length} of its blasts {pending.length === 1 ? 'is' : 'are'} still waiting to be
                  sent — send {pending.length === 1 ? 'it' : 'them'} from the Outbox or Bulk first, or
                  Pause this instead. Cannot delete while one is unsent.
                </span>
              ) : confirmDelete === c.id ? (
                <>
                  <span className="status">
                    End "{c.name || c.id}" for good? Anything it already sent stays sent and on
                    record — this only stops it firing again.
                  </span>
                  <button className="btn" disabled={!!busy} onClick={() => void remove(c.id)}>
                    {busy === c.id + 'del' ? 'Deleting…' : 'Yes, delete it'}
                  </button>
                  <button className="btn ghost" onClick={() => setConfirmDelete(null)}>
                    Keep it
                  </button>
                </>
              ) : (
                <button className="btn ghost sm" disabled={!!busy} onClick={() => setConfirmDelete(c.id)}>
                  Delete this
                </button>
              )}
            </div>

            <h3 style={{ marginTop: 16 }}>What it has produced</h3>
            {mine.length === 0 && <div className="hint">Nothing generated yet.</div>}
            {mine.map((g) => {
              const occ = occurrences.get(g.id)
              return (
                <div className="entry" key={g.id}>
                  <div className="emeta">
                    <span>{formatDate(g.created_at)}</span>
                    <span className={`pill s-${occ?.status ?? 'held'}`}>{occ?.status ?? 'waiting for review'}</span>
                  </div>
                  <div className="dsubject">{g.subject}</div>
                  <div className="hint">
                    {occ?.recipient_count != null
                      ? `${occ.recipient_count} recipients`
                      : `${g.audience?.agent_ids?.length ?? 0} agents planned`}{' '}
                    · generated {formatDateTime(g.created_at)}
                  </div>
                </div>
              )
            })}
          </section>
        )
      })}
    </div>
  )
}
